/**
 * Implementation of Monitors list component
 *
 * @author - Harsh Tomar
 */

// Dependencies
import { useMutation, useQuery } from "react-query";
import React, { useState } from "react";
import { MdOutlineDesktopWindows } from "react-icons/md";
import { VscJson } from "react-icons/vsc";
import { Link } from "react-router-dom";
import {
  createAPing,
  pauseAMonitor,
  activateAMonitor,
  deleteAMonitor,
} from "../../api/reactQuery";
import { Else, If, Then, When, Switch as IFSwitch, Case } from "react-if";
import { getMonitors } from "../../api/requests";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { BiLoaderAlt } from "react-icons/bi";
import { IoIosPause, IoIosRefresh, IoIosPlay, IoIosLock, IoIosTrash, IoMdMore } from "react-icons/io";
import { FaExpeditedssl } from "react-icons/fa";
import { IoLockClosedOutline } from "react-icons/io5";
import { HiOutlineLockClosed, HiOutlineLockOpen, HiOutlineExclamation } from "react-icons/hi";
import { MonitorsResponse } from "../../api/responses";
import { toast } from "react-hot-toast";
import MonitorActionMenu from "../MonitorActionMenu";
import { useModal } from "../../hooks/useModal";

dayjs.extend(relativeTime);

const resolveIconBgColor = (status: STATUS) => {
  switch (status) {
    case "ACTIVE":
      return "bg-accent";
    case "PAUSED":
      return "bg-gray-500";
    case "EXPIRING_SOON":
      return "bg-orange-500"
    case "EXPIRED":
      return "bg-red-500"
    case "FAILED":
      return "bg-red-500"
  }
}

const resolveIconColor = (status: STATUS) => {
  switch (status) {
    case "ACTIVE":
      return "text-accent"
    case "PAUSED":
      return "text-gray-500"
    case "EXPIRING_SOON":
      return "text-orange-500"
    case "EXPIRED":
      return "text-red-500"
    case "FAILED":
      return "text-red-500"

  }
}


const resolveTextColor = (status: STATUS) => {
  switch (status) {
    case "ACTIVE":
      return "text-accent"
    case "PAUSED":
      return "text-gray-500"
    case "EXPIRING_SOON":
      return "text-orange-500"
    case "EXPIRED":
      return "text-red-500"
    case "FAILED":
      return "text-red-500"

  }
}

const monitorIcon = (status: STATUS) => {

  return {

    WEBSITE: (
      <div className="mr-4 flex justify-center items-center rounded-lg bg-sky-600 bg-opacity-10 w-10 h-10">
        <MdOutlineDesktopWindows size={24} className="text-sky-600" />
      </div>
    ),
    API: (
      <div className="mr-4 flex justify-center items-center rounded-lg bg-violet-500 bg-opacity-10 w-10 h-10">
        <VscJson size={32} className="text-violet-500" />
      </div>
    ),
    SSL_MONITORING: (
      <div className={`mr-4 flex justify-center items-center rounded-lg bg-opacity-10 w-9 h-9 ${resolveIconBgColor(status)}`}>
        {status === "EXPIRED" || status === "FAILED" ? <HiOutlineExclamation size={24} className={resolveIconColor(status)} /> : <HiOutlineLockClosed size={24} className={resolveIconColor(status)} />}
      </div>
    ),
  }
};

type TYPE = "API" | "WEBSITE" | "SSL_MONITORING";
type STATUS = "ACTIVE" | "PAUSED" | "EXPIRING_SOON" | "EXPIRED" | 'FAILED';
type MonitorRowProps = {
  type: TYPE;
  key: string;
  name: string;
  id: string;
  url: string;
  status: STATUS;
  issuedBy: string;
  expiringOn: string;
  createdAt: string;
  pingFailed: boolean;
  pingError: string;
  pingErrorSeverity: string;
};

function MonitorRow(props: MonitorRowProps) {
  const [pinging, setPinging] = useState(false);
  const [loading, setLoading] = useState(false);

  const { close, open} = useModal();
  const { refetch } = useQuery("monitors", () => getMonitors(), {
    enabled: false,
  });

  const pauseAMonitorMutation = useMutation(
    (variables: { monitorId: string }) => pauseAMonitor(variables.monitorId),
    {
      onError: (error) => {
        setLoading(false);
        toast.error(error as string);
      },
      onSuccess: () => {
        toast.success(`SSL monitoring for ${props.url} is paused now`)
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        refetch();
      },
    }
  );

  const deleteAMonitorMutation = useMutation(( variables: { monitorId: string })=> deleteAMonitor(variables.monitorId),{
    onError:(error)=>{
      toast.error(`Failed to delete ${props.url}`)
      setLoading(false);
    },
    onSuccess: ()=>{
      toast.success(`SSL Monitoring at ${props.url} has been successfully deleted.`)
      setTimeout(() => {
        setLoading(false);
      }, 1000);
      refetch();
    }
  });

  const activateAMonitorMutation = useMutation(
    (variables: { monitorId: string }) => activateAMonitor(variables.monitorId),
    {
      onError: (error) => {
        setLoading(false);
        toast.error(error as string)
      },
      onSuccess: () => {
        toast.success(`SSL monitoring for ${props.url} is active now.`)
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        refetch();
      },
    }
  );

  const createPingMutation = useMutation(
    (variables: { monitorId: string }) => createAPing(variables.monitorId),
    {
      onError: (error) => {
        setPinging(false);
        toast.error(error as string)
      },
      onSuccess: () => {
        setPinging(false);
        refetch();
        toast.success(`Refetched certificate details for ${props.url}`)
      },
    }
  );

  const onClickCheckNow = (id: string) => {
    createPingMutation.mutate({
      monitorId: id,
    });
   
    setPinging(true);
  };

  const onClickDeleteAMonitor = (id: string) =>{ 
    deleteAMonitorMutation.mutate({
      monitorId: id
    })
    close();
    setLoading(true);
  }

  const onClickPause = (id: string) => {
    pauseAMonitorMutation.mutate({
      monitorId: id,
    });

    setLoading(true);
  };
  const onClickActivate = (id: string) => {
    activateAMonitorMutation.mutate({
      monitorId: id,
    });

    setLoading(true);
  };


  const status = props.pingErrorSeverity === 'FATAL' ? 'FAILED' : props.status
  return (
    <div className="flex items-center py-3" key={props.id}>
      <div className="px-2 w-1/3 flex items-center">
        {monitorIcon(status)[props.type]}
        <div>
          <p>{props.name}</p>
          <p className="text-gray-500 font-semibold">{props.url}</p>
          <When condition={ props.status !== "PAUSED" && props.pingError}>
          <h4 className={`text-xs ${props.pingErrorSeverity === "FATAL" ? 'text-red-500' : 'text-orange-500'}`}>{props.pingError}</h4>
          </When>
        </div>
      </div>
      <When condition={props.status === "EXPIRED"}>
        <div className="text-center w-1/5 flex justify-center">
          <div className="bg-red-500 bg-opacity-20 px-2 py-1 rounded">
            <h3 className="text-sm text-red-500">{props.status}</h3>
          </div>
        </div>
      </When>
      <When condition={props.status === "PAUSED"}>
        <div className="text-center w-1/5 flex justify-center">
          <div className="bg-gray-500 bg-opacity-20 px-2 py-1 rounded">
            <h3 className="text-sm text-gray-500">{props.status}</h3>
          </div>
        </div>
      </When>
      <When condition={props.status === "ACTIVE"}>
        <div className="text-center w-1/5 flex justify-center">
          <div className="bg-green-500 bg-opacity-20 px-2 py-1 rounded">

            <h3 className="text-sm text-green-500">{props.status}</h3>

          </div>
    
        </div>
      </When>
      <When condition={props.status === "EXPIRING_SOON"}>
        <div className="text-center w-1/5 flex justify-center">
          <div className="bg-orange-500 bg-opacity-20 px-2 py-1 rounded">
            <h3 className="text-sm text-orange-500">{props.status}</h3>
          </div>
        </div>
      </When>
      <div className="text-center w-1/5">
        <h3 className="text-gray-700">{props.issuedBy}</h3>
      </div>

      <div className="text-center w-1/5">
        <If condition={props.status === "EXPIRED"}>
          <Then>
            <h3 className="text-red-700">{props.expiringOn}</h3>
          </Then>
          <Else>
            <h3 className="text-gray-700">{props.expiringOn}</h3>
          </Else>
        </If>
      </div>
      <div className="text-center w-1/5">
        <h3 className="text-gray-700">{props.createdAt}</h3>
      </div>
      <div className="text-center w-1/4 flex justify-end pr-4">
        <If condition={loading}>
          <Then>
            <div className="flex justify-center items-center">
              <div className="mr-4">
                <div className="flex justify-center items-center">
                  <BiLoaderAlt className="animate-spin" size={28} />
                </div>
              </div>
            </div>
          </Then>
          <Else>
            <When condition={props.status === "PAUSED"}>
              <button
                onClick={(e) => {
                  onClickActivate(props.id);
                }}
                className="btn-primary-outline py-1"
              >
                <IoIosPlay />
              </button>
              <div className="ml-4">
                  <button
                    onClick={(e) => {
                      onClickCheckNow(props.id);
                    }}
                    className="btn-primary-outline py-1 px-0.5"
                  >
                    <IoMdMore />
                  </button>
                </div>
            </When>
            <When condition={props.status === "ACTIVE" || props.status === "EXPIRING_SOON" || props.status === "EXPIRED"}>
              <div className="flex">
                <div className="mr-4">
                  <button
                    onClick={(e) => {
                      onClickPause(props.id);
                    }}
                    className="btn-primary-outline py-1"
                  >
                    <IoIosPause />
                  </button>
                </div>
                <div>
                  <button
                    onClick={(e) => {
                      onClickCheckNow(props.id);
                    }}
                    className="btn-accent-outline py-1"
                  >
                    <IoIosRefresh />
                  </button>
                </div>
                <div className="ml-4">
                    <MonitorActionMenu options={[
                      <button onClick={()=>{
                        open({ onClose: close, component: <div>

                        <h3 className="text-xl text-center">Delete monitor</h3>
                        <div className="mt-2">
                        <h3 className=" text-gray-800 text-center">Are you sure you want to permanently delete monitor for <strong>{props.url}</strong>. We will stop monitoring and all your data will be lost.</h3>
                        </div>
                          <div className="mt-6 flex justify-center">
                            <button onClick={close} className=" py-1 px-4 mx-5 text-gray-700 bg-gray-100 rounded-lg">Cancel</button>
                            <button  onClick={()=>{
                              onClickDeleteAMonitor(props.id)
                            }} className="btn-primary-outline text-red-500 border-red-500 hover:bg-red-500 hover:text-white py-1 px-4">Delete permanently</button>   
                          </div>
                        </div>})
                      }} className="py-1 px-1 w-full box-border rounded-sm hover:bg-red-50 text-red-500"><div className="flex items-center"><IoIosTrash size={20}/>&nbsp;Delete&nbsp;monitor</div></button>
                    ]} className="btn-primary-outline py-1 px-0.5">
                      
                      <IoMdMore />

                    </MonitorActionMenu>
                </div>
              </div>
            </When>
          </Else>
        </If>
      </div>
    </div>
  );
}

type Props = {
  data: MonitorsResponse[];
};


function getPingError(error:string){
  switch(error) {
    case 'ENOTFOUND':
      return {message: 'Domain not found', type: 'FATAL'}

    case 'EPROTO':
      return {message: 'Error in domain', type: 'FATAL'}

    case 'CERT_HAS_EXPIRED':
      return {message: 'SSL certificate is expired', type: 'FATAL'}
    case 'EHOSTUNREACH':
      return { message: 'Domain not found', type: 'FATAL'}  
    case 'SELF_SIGNED_CERT_IN_CHAIN':
      return {message: 'SSL certificate is self signed', type: 'WARNING'}
    default:
      return {message: null, type: null};
  }
}

export default function AllMonitors(props: Props) {
  return (
    <>
      <div className="flex items-center bg-gray-100 py-2 px-2 rounded-lg">
        <div className="w-1/3 px-4">
          <p className="font-medium">Domain</p>
        </div>
        <div className="w-1/5">
          <p className="text-center font-medium">Status</p>
        </div>
        <div className="w-1/5">
          <p className="text-center font-medium">Issuer</p>
        </div>

        <div className="px-4 w-1/5">
          <p className="text-center font-medium">Expiring Date</p>
        </div>

        <div className="px-4 w-1/5">
          <p className="text-center font-medium">Last checked</p>
        </div>
        <div className="px-4 w-1/4">
          <p className="text-center font-medium"></p>
        </div>
      </div>

      {props.data.map((monitor) => {
        const lastestPing = monitor?.latestPing;

        const issuedBy = lastestPing?.meta?.issuedBy;
        const expiringOn = new Date(lastestPing?.meta?.validTo).toDateString();
        const createdAt = lastestPing?.createdAt;
        const isPingFailed = lastestPing?.status === "FAILURE" || !!lastestPing?.meta?.authorizationError
        console.log(lastestPing);

        // ENOTFOUND
        // EPROTO
        const pingError = getPingError( lastestPing?.meta?.code || lastestPing?.meta?.authorizationError ).message || '';
        const pingErrorSeverity = getPingError( lastestPing?.meta?.code || lastestPing?.meta?.authorizationError ).type || '';
        const createdAtString = dayjs(createdAt).fromNow();

        return (
          <MonitorRow
            id={monitor._id}
            key={monitor._id}
            type={monitor.type}
            name={""}
            url={monitor.url}
            status={monitor.status}
            issuedBy={issuedBy}
            expiringOn={expiringOn}
            createdAt={createdAtString}
            pingFailed={isPingFailed}
            pingError={pingError}
            pingErrorSeverity={pingErrorSeverity}
          />
        );
      })}
    </>
  );
}
